import React, {FC, memo, useState} from 'react';
import {Flex} from '@priority-software/priority-style-react';
import styles from './styles.module.scss';
import {ToolbarPlugin} from '../ToolbarPlugin/ToolbarPlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import {useEditor} from '../hooks/useEditor';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {ExposeEditorInstancePlugin} from '../../../../../Form/components/HTMLFormEditor/Editor/plugins/ExposeEditorInstancePlugin';
import {
    AutoLinkPlugin,
    DragDropPaste,
    HistoryPlugin,
    HorizontalRulePlugin,
    ImagesPlugin,
    LinkPlugin,
    ListPlugin
} from '../../../../../Form/components/HTMLFormEditor/Editor/plugins';
import {EmojisPlugin} from '../../../../../Form/components/HTMLFormEditor/Editor/plugins/EmojisPlugin';
import {CheckListPlugin} from '@lexical/react/LexicalCheckListPlugin';
import {InsertHtmlAndImagesPlugin} from './plugins/InsertHtmlAndImagesPlugin';
import {ThemeUpdater} from './plugins/ThemeUpdater';
import {DisableNestingPlugin} from './plugins/DisableNestingPlugin';
import cx from 'classnames';
import {CustomPastePlugin} from './plugins/CustomPastePlugin';
import {ETargetAutoFill} from '../../types';
import {useHandlePressKey} from '../hooks/useHandleKeyPress';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {useBrowserContextMenu} from '../hooks/useBrowserContextMenu';

interface IEditorProps {
    targetAutoFill: React.MutableRefObject<ETargetAutoFill>;
}

export const Editor: FC<IEditorProps> = memo(({targetAutoFill}) => {
    const {
        editorDivRef,
        states,
        editorConfig,
        setEditorInstance,
        listitem,
        setFocusSubject,
        isFromTextEditor
    } = useEditor();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    useHandlePressKey({target: editorDivRef.current, isDropdownOpen, setIsDropdownOpen});
    useBrowserContextMenu();

    return (
        <LexicalComposer key={states.language} initialConfig={editorConfig.current}>
            <Flex
                {...createDataTestId(`email-editor-container`, true)}
                direction="column"
                componentClasses={cx(styles.flexContainer, styles.gap)}
                style={{overflow: 'hidden'}}
                alignItems="flex-start"
                justifyContent="flex-start"
            >
                <ToolbarPlugin {...{setIsDropdownOpen}} />
                <RichTextPlugin
                    key={states.language}
                    contentEditable={
                        <>
                            <ContentEditable
                                {...createDataTestId(`email-editor-content-editable`, true)}
                                onFocus={() => {
                                    setFocusSubject(false);
                                    targetAutoFill.current = ETargetAutoFill.EDITOR;
                                }}
                                className={cx(styles.editorInput, styles.flexContainer)}
                                ref={editorDivRef}
                                contentEditable={true}
                            />
                        </>
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                />

                <ExposeEditorInstancePlugin callback={setEditorInstance} />
                <ImagesPlugin />
                <EmojisPlugin />
                <LinkPlugin />
                <AutoLinkPlugin />
                <HistoryPlugin externalHistoryState={null} delay={500} />
                <ListPlugin />
                <CheckListPlugin />
                <HorizontalRulePlugin />
                <InsertHtmlAndImagesPlugin
                    htmlString={states[states.language]?.emailHtml}
                    isFromTextEditor={isFromTextEditor}
                />
                <ThemeUpdater {...{listitem}} />
                <DisableNestingPlugin />
                <CustomPastePlugin />
                <DragDropPaste isDraggable={false} shouldCheckSize={true} />
            </Flex>
        </LexicalComposer>
    );
});

export default Editor;
